// Pure helpers for the proposal review surface (standing-agent-ui "Batch
// decisions and execution", "Live updates"): the footer tally, what "Approve all
// remaining" would and would NOT approve, and the polite announcements a live
// refresh produces.

import type { BatchCounts, Proposal, ProposalBatch } from '@shared/api-types';
import { PROPOSAL_STATUS } from '../components/statusMeta';

/** "2 approved · 1 denied · 1 pending", or "2 done · 1 failed" once things ran. */
export function tallyText(proposals: Proposal[]): string {
  const count = (pred: (p: Proposal) => boolean) => proposals.filter(pred).length;
  const ran = count((p) => p.status === 'done' || p.status === 'failed' || p.status === 'running');
  const parts: [number, string][] = ran
    ? [
        [count((p) => p.status === 'done'), 'done'],
        [count((p) => p.status === 'failed'), 'failed'],
        [count((p) => p.status === 'running'), 'running'],
        [count((p) => p.status === 'approved' || p.status === 'autoApproved'), 'approved'],
        [count((p) => p.status === 'denied'), 'denied'],
        [count((p) => p.status === 'pending'), 'pending'],
        [count((p) => p.status === 'expired'), 'expired'],
      ]
    : [
        [count((p) => p.status === 'approved' || p.status === 'autoApproved'), 'approved'],
        [count((p) => p.status === 'denied'), 'denied'],
        [count((p) => p.status === 'pending'), 'pending'],
        [count((p) => p.status === 'expired'), 'expired'],
      ];
  const shown = parts.filter(([n], i) => n > 0 || (!ran && i < 3));
  return shown.map(([n, label]) => `${n} ${label}`).join(' · ');
}

export interface BulkPlan {
  /** Pending proposals "Approve all remaining" would approve. */
  eligible: Proposal[];
  /** [tool, count] for the eligible ones, most first. */
  byTool: [string, number][];
  /** Pending proposals left for individual review (high risk / always asks / untrusted). */
  held: Proposal[];
}

/**
 * The server decides what may be approved in bulk (`needsIndividualReview`);
 * the agent's own risk label never widens it. High risk is held too.
 */
export function bulkApprovalPlan(proposals: Proposal[]): BulkPlan {
  const pending = proposals.filter((p) => p.status === 'pending');
  const held = pending.filter((p) => p.needsIndividualReview || p.alwaysAsks || p.risk === 'high');
  const eligible = pending.filter((p) => !held.includes(p));
  const counts = new Map<string, number>();
  for (const p of eligible) counts.set(p.tool, (counts.get(p.tool) ?? 0) + 1);
  const byTool = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return { eligible, byTool, held };
}

/** "3 proposals: 2 × ticket.add_note, 1 × ninja.reboot_device". */
export function bulkSummary(plan: BulkPlan): string {
  const n = plan.eligible.length;
  return `${n} proposal${n === 1 ? '' : 's'}: ${plan.byTool.map(([tool, c]) => `${c} × ${tool}`).join(', ')}`;
}

/**
 * What changed between two loads of the same batch, phrased for a screen
 * reader: "Proposal 2 done", "Proposal 1 approved by Dana". Nothing for the
 * first load.
 */
export function announcements(prev: ProposalBatch | null, next: ProposalBatch): string[] {
  if (!prev || prev.id !== next.id) return [];
  const before = new Map(prev.proposals.map((p) => [p.id, p.status]));
  const ordered = [...next.proposals].sort((a, b) => a.ordinal - b.ordinal);
  const out: string[] = [];
  ordered.forEach((p, i) => {
    const was = before.get(p.id);
    if (was === undefined || was === p.status) return;
    const label = PROPOSAL_STATUS[p.status]?.label.toLowerCase() ?? p.status;
    const by = (p.status === 'approved' || p.status === 'denied') && p.decidedByName ? ` by ${p.decidedByName}` : '';
    out.push(`Proposal ${i + 1} ${label}${by}`);
  });
  return out;
}

/** Decisions can be undone until the approved actions start. */
export function isUndoable(batch: ProposalBatch): boolean {
  return batch.actRunId == null && !batch.proposals.some((p) => p.status === 'running' || p.status === 'done' || p.status === 'failed');
}

/** The same tally from a batch's server-side counts (list views, where proposals aren't loaded). */
export function countsText(c: BatchCounts): string {
  const ran = c.done + c.failed + c.running > 0;
  const parts: [number, string][] = ran
    ? [[c.done, 'done'], [c.failed, 'failed'], [c.running, 'running'], [c.approved, 'approved'], [c.denied, 'denied'], [c.pending, 'pending'], [c.expired, 'expired']]
    : [[c.approved, 'approved'], [c.denied, 'denied'], [c.pending, 'pending'], [c.expired, 'expired']];
  return parts
    .filter(([n], i) => n > 0 || (!ran && i < 3))
    .map(([n, label]) => `${n} ${label}`)
    .join(' · ');
}
